import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '~/features/account/SessionProvider';
import { Button, Spinner } from '~/shared/ui';

/**
 * Explicit sign-in landing — Cloudflare is the only marketplace identity provider.
 */
export function SignInPage(): JSX.Element {
  const { isSignedIn, isLoading, signIn } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(params.get('returnTo'));

  if (isLoading) {
    return (
      <div className="grid place-items-center py-24 text-slate-500">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (isSignedIn) {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <div className="mx-auto grid max-w-5xl gap-8 py-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:py-10">
      <div className="animate-fade-up">
        <p className="eyebrow">Secure sign-in</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
          Continue with Cloudflare
        </h1>
        <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-400">
          Install applications into your own account with a scoped OAuth grant. Publishers can connect
          GitHub later from Account when they are ready to submit source.
        </p>
        <ul className="mt-8 space-y-3 text-sm text-slate-300">
          <TrustPoint>No API tokens pasted into forms</TrustPoint>
          <TrustPoint>Permissions requested only for install &amp; deploy</TrustPoint>
          <TrustPoint>You can disconnect accounts anytime from Account</TrustPoint>
        </ul>
      </div>

      <div className="panel animate-fade-up space-y-5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-brand to-brand-dim text-lg text-slate-950 shadow-glow"
          >
            ◆
          </span>
          <div>
            <p className="font-semibold text-slate-100">WorkerHub</p>
            <p className="text-xs text-slate-500">Sign in to install or publish</p>
          </div>
        </div>

        <Button
          variant="primary"
          className="w-full py-3 text-base"
          onClick={() => {
            signIn(returnTo);
          }}
        >
          Continue with Cloudflare
        </Button>

        <p className="text-xs leading-relaxed text-slate-500">
          You will leave briefly to approve access on Cloudflare, then return here signed in.
        </p>

        <div className="soft-divider pt-4">
          <button
            type="button"
            className="text-sm text-slate-500 transition-colors hover:text-slate-300"
            onClick={() => {
              navigate(returnTo === '/signin' ? '/' : returnTo);
            }}
          >
            ← Back to browsing
          </button>
        </div>
      </div>
    </div>
  );
}

function TrustPoint({ children }: { children: string }): JSX.Element {
  return (
    <li className="flex items-start gap-2.5">
      <span aria-hidden="true" className="mt-0.5 text-brand-soft">
        ✓
      </span>
      <span>{children}</span>
    </li>
  );
}

function safeReturnTo(value: string | null): string {
  if (value === null || value === '') return '/';
  if (!value.startsWith('/') || value.startsWith('//')) return '/';
  return value.slice(0, 512);
}
